import React, { useRef, useState } from 'react';
import { FaFilePdf, FaPaperclip, FaTrashAlt } from 'react-icons/fa';
import apiService from '../services/apiService';
import {
  ATTACHMENT_ACCEPT,
  ALLOWED_TYPES_LABEL,
  MAX_FILES_PER_PAYMENT,
  MAX_FILES_PER_UPLOAD,
  MAX_FILE_BYTES,
  formatFileSize,
  isImageAttachment,
  validateAttachmentSelection,
} from '../utils/paymentAttachments';
import { Label } from '../styles/ComponentStyles';

// The receipts attached to one payment: what is already there, and the way to
// add more or take one away.
//
// Rules of the house:
//   - every limit is checked here first, so a rejection is instant and explains
//     itself instead of arriving as a 4xx after a slow upload;
//   - files are staged, not uploaded on selection, so the user sees what they
//     picked and a failed upload leaves the selection intact to retry;
//   - removal asks first, with the same inline Confirm/Cancel swap used for
//     deletes elsewhere in the app rather than window.confirm;
//   - nothing is disabled without a sentence next to it saying why.
//
// `attachments` is the current list and `onChange` receives the full list the
// server answers with, so the parent stays the owner of the data.

const panelStyle = {
  marginTop: 20,
  marginLeft: 20,
  padding: '1.5rem',
  maxWidth: 500,
  width: '100%',
  borderRadius: 8,
  border: '1px solid #ccc',
  backgroundColor: '#333131',
  color: '#fff',
  boxSizing: 'border-box',
};

const buttonStyle = (background) => ({
  padding: '8px 16px',
  background,
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '1rem',
});

const smallButtonStyle = (color) => ({
  background: 'none',
  border: 'none',
  color,
  cursor: 'pointer',
  padding: '0 6px',
  fontSize: '0.875rem',
});

const tileStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  marginBottom: 8,
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
};

const thumbStyle = {
  width: 56,
  height: 56,
  objectFit: 'cover',
  borderRadius: 4,
  border: '1px solid #555',
  backgroundColor: '#1e1e1e',
  display: 'block',
};

const nameStyle = {
  color: '#9ec5fe',
  wordBreak: 'break-all',
  textDecoration: 'none',
};

const errorStyle = { color: '#f44336', margin: '4px 0' };
const hintStyle = { color: '#bbb', fontSize: '0.8rem', margin: '6px 0 0' };

const attachmentLabel = (attachment, index) =>
  attachment?.name || `Attachment ${index + 1}`;

const PaymentAttachments = ({ paymentId, attachments = [], onChange, onFlashMessage }) => {
  const fileInputRef = useRef(null);
  const [staged, setStaged] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const list = Array.isArray(attachments) ? attachments : [];
  const atMax = list.length >= MAX_FILES_PER_PAYMENT;
  const busy = uploading || removingId !== null;

  const announce = (message) => {
    if (onFlashMessage && message) onFlashMessage(message);
  };

  const applyResult = (result) => {
    if (onChange) {
      onChange(Array.isArray(result?.attachments) ? result.attachments : list);
    }
  };

  const handleSelect = (e) => {
    const { accepted, errors: problems } = validateAttachmentSelection(
      e.target.files,
      list.length
    );
    setErrors(problems);
    setStaged(accepted);
    // Clearing the input means re-picking the same file fires onChange again;
    // the File objects themselves are already held in `staged`.
    e.target.value = '';
  };

  const handleClearSelection = () => {
    setStaged([]);
    setErrors([]);
  };

  const handleUpload = async () => {
    if (staged.length === 0) return;
    setUploading(true);
    setErrors([]);
    try {
      const result = await apiService.uploadPaymentAttachments(paymentId, staged);
      applyResult(result);
      setStaged([]);
      announce(
        result?.message ||
          `Attached ${staged.length} file${staged.length === 1 ? '' : 's'}.`
      );
    } catch (err) {
      // The selection is deliberately kept so the upload can simply be retried.
      setErrors([err?.message || 'Failed to upload attachments.']);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (attachmentId) => {
    setRemovingId(attachmentId);
    setErrors([]);
    try {
      const result = await apiService.deletePaymentAttachment(paymentId, attachmentId);
      applyResult(result);
      setConfirmingId(null);
      announce(result?.message || 'Attachment removed.');
    } catch (err) {
      setErrors([err?.message || 'Failed to remove attachment.']);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={panelStyle}>
      <h3 style={{ marginTop: 0 }}>
        <FaPaperclip style={{ marginRight: 8 }} />
        Receipts &amp; Screenshots ({list.length}/{MAX_FILES_PER_PAYMENT})
      </h3>

      {list.length === 0 ? (
        <p style={{ color: '#bbb', marginTop: 0 }}>Nothing attached to this payment yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
          {list.map((attachment, index) => {
            const id = attachment?.attachment_id;
            const label = attachmentLabel(attachment, index);
            const removing = removingId === id;
            return (
              <li key={id || label} style={tileStyle}>
                <a href={attachment?.url} target="_blank" rel="noopener noreferrer" title={label}>
                  {isImageAttachment(attachment) ? (
                    <img src={attachment?.url} alt={label} style={thumbStyle} />
                  ) : (
                    <span
                      style={{
                        ...thumbStyle,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#f44336',
                        fontSize: 26,
                      }}
                    >
                      <FaFilePdf />
                    </span>
                  )}
                </a>
                <span style={{ flexGrow: 1, minWidth: 0 }}>
                  <a
                    href={attachment?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={nameStyle}
                  >
                    {label}
                  </a>
                  {attachment?.size ? (
                    <span style={{ display: 'block', color: '#bbb', fontSize: '0.75rem' }}>
                      {formatFileSize(attachment.size)}
                    </span>
                  ) : null}
                </span>
                {confirmingId === id ? (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => handleRemove(id)}
                      disabled={removing}
                      style={smallButtonStyle('#f44336')}
                    >
                      {removing ? 'Removing...' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingId(null)}
                      disabled={removing}
                      style={smallButtonStyle('#bbb')}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingId(id)}
                    style={smallButtonStyle('#f44336')}
                    title={`Remove ${label}`}
                    aria-label={`Remove ${label}`}
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Label htmlFor="payment-attachments">Add receipts or screenshots</Label>
      <input
        id="payment-attachments"
        ref={fileInputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        onChange={handleSelect}
        disabled={atMax || busy}
      />
      <p style={hintStyle}>
        {atMax
          ? `This payment is at the maximum of ${MAX_FILES_PER_PAYMENT} attachments — remove one to add another.`
          : `Up to ${MAX_FILES_PER_UPLOAD} files at a time, ${MAX_FILES_PER_PAYMENT} per payment, ${formatFileSize(MAX_FILE_BYTES)} each. ${ALLOWED_TYPES_LABEL}.`}
      </p>

      {staged.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <strong>Ready to upload:</strong>
          <ul style={{ margin: '6px 0 10px', paddingLeft: 20 }}>
            {staged.map((file) => (
              <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                {file.name} <span style={{ color: '#bbb' }}>({formatFileSize(file.size)})</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading}
            style={{ ...buttonStyle('#28a745'), opacity: uploading ? 0.6 : 1, marginRight: 10 }}
          >
            {uploading
              ? 'Uploading...'
              : `Upload ${staged.length} file${staged.length === 1 ? '' : 's'}`}
          </button>
          <button
            type="button"
            onClick={handleClearSelection}
            disabled={uploading}
            style={buttonStyle('#555')}
          >
            Clear
          </button>
        </div>
      )}

      {errors.map((message) => (
        <p key={message} style={errorStyle}>
          {message}
        </p>
      ))}
    </div>
  );
};

export default PaymentAttachments;
